import type { Orchard, SpraySession } from '../services/sprayService';

/**
 * Display-time helpers for the single-orchard invariant introduced in the
 * revise_spray_session_20260414 track.
 *
 * New spray sessions are constrained to exactly one orchard, but legacy
 * records may still carry a multi-orchard orchardIds array in the database
 * (no migration was run). These helpers return the first orchard of the
 * array as "the" orchard so UI and reports can show a single name without
 * caller-side branching.
 */

type SessionWithVirtual = SpraySession & { primaryOrchardId?: string | null };

/**
 * Returns the primary orchard's ID for a session, falling back to
 * orchardIds[0] when the backend has not supplied the primaryOrchardId
 * virtual. Returns null when no orchard is present.
 */
export function getPrimaryOrchardId(session: SessionWithVirtual): string | null {
  if (session.primaryOrchardId) return session.primaryOrchardId;
  if (Array.isArray(session.orchardIds) && session.orchardIds.length > 0) {
    return session.orchardIds[0];
  }
  return null;
}

/**
 * Resolves the primary orchard's name by looking up the primary orchard ID
 * in the supplied orchard list. Returns null when the session has no primary
 * orchard or the orchard is not present in the list (e.g. soft-deleted).
 */
export function getPrimaryOrchardName(
  session: SessionWithVirtual,
  orchards: Orchard[]
): string | null {
  const id = getPrimaryOrchardId(session);
  if (!id) return null;
  const match = orchards.find(o => o._id === id);
  return match ? match.orchardName : null;
}

/**
 * Resolves the primary orchard record (not just the name) so callers can
 * read related phenology data like activePetalFallDate. Returns null when
 * the session has no primary orchard or the orchard is not in the list.
 */
export function getPrimaryOrchard(
  session: SessionWithVirtual,
  orchards: Orchard[]
): Orchard | null {
  const id = getPrimaryOrchardId(session);
  if (!id) return null;
  return orchards.find(o => o._id === id) ?? null;
}
